/**
 * Type guard for string values.
 *
 * Returns true for strings, false for all other types.
 */
export const isString = (value: unknown): value is string => typeof value === 'string';

/**
 * Type guard for plain objects.
 *
 * Returns true for objects, false for arrays, null, and primitives.
 */
export const isObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);

/**
 * Extracts string keys from a type, filtering out symbol and number keys.
 *
 * @example
 * ```typescript
 * interface User { name: string; age: number; }
 * type UserKeys = Keys<User>; // 'name' | 'age'
 * ```
 */
export type Keys<Type> = Extract<keyof Type, string>;

/**
 * Type-safe property check.
 *
 * Type guard that narrows key to valid object property.
 * Uses Object.prototype.hasOwnProperty for reliability.
 */
export const hasOwnProperty = <Type extends object>(
  obj: Type,
  key: PropertyKey
): key is Keys<Type> => Object.prototype.hasOwnProperty.call(obj, key);
